import { useEffect, useRef } from 'react';
import { ArrowUpRight, Check, Mail, X } from 'lucide-react';
import ConversionCalculator from '../components/aerzte/ConversionCalculator';
import GrowthSystemVisual from '../components/aerzte/GrowthSystemVisual';
import { EvidenceSection, ReferenceWall } from '../components/aerzte/ProofSection';
import { ConfessionSection, RiskReversalSection } from '../components/aerzte/JourneySections';
import {
  ComparisonSection,
  FounderSection,
  IncludedSection,
  ProcessSection,
} from '../components/aerzte/OfferSections';

/* Die Seite, auf die Werbung zeigt.
 *
 * Sie verkauft die Leistung von Make It Media, nicht die Software dahinter.
 * Die Software ist das Werkzeug, mit dem geliefert wird - sie wird hier
 * bewusst nicht beim Namen genannt, weil der Arzt keine Software kauft.
 *
 * Nexbit liefert die große Produktbuehne, die ueberschneidenden Systemkarten
 * und den technischen Rhythmus. Cluster liefert Ruhe fuer laengere Inhalte.
 * Farben und Sprache bleiben eindeutig Make It Media.
 */

const SCHRIFTEN = 'https://api.fontshare.com/v2/css?f[]=satoshi@500,600,700&f[]=general-sans@400,500,600&display=swap';
const HERO_VIDEO = 'https://assets.mixkit.co/videos/4834/4834-720.mp4';
const HERO_POSTER = 'https://assets.mixkit.co/videos/4834/4834-thumb-720-0.jpg';
const SANS = '"General Sans", "Helvetica Neue", Arial, sans-serif';
const DISPLAY = '"Satoshi", "General Sans", sans-serif';

const FUER_WEN = [
  'Sie bieten ästhetische Selbstzahlerbehandlungen an oder bauen den Bereich gerade auf',
  'Sie haben Kapazität, die Sie planbar füllen wollen',
  'Sie führen Ihre Termine bereit in einem System oder sind bereit dazu',
];

const NICHT_FUER = [
  'Sie suchen nur einen Terminkalender',
  'Sie wollen Anfragen kaufen, ohne am Ablauf dahinter etwas zu ändern',
  'Ihre Termine sollen weiterhin in drei getrennten Systemen liegen',
];

const FRAGEN = [
  {
    frage: 'Wir haben eigentlich schon genug Software.',
    antwort: 'Das hören wir oft, und meistens stimmt es. Wir ersetzen Ihre Praxissoftware für Dokumentation und Abrechnung nicht. Wir schließen die Lücke davor: von der Anzeige über die Anfrage bis zur Beratung. Genau die deckt heute kein System bei Ihnen ab.',
  },
  {
    frage: 'Wird der Umstieg kompliziert?',
    antwort: 'Sie machen ihn nicht allein. Wir richten Leistungen, Formulare, Mitarbeiter und die Buchungsseite gemeinsam mit Ihnen ein und übernehmen Ihre Daten aus dem bisherigen Tool kostenlos. Eine Praxis hat es so gesagt: „Ihr habt mir alles eingerichtet, bis hin zu den E-Mail-Adressen.“',
  },
  {
    frage: 'Agenturen versprechen viel. Warum sollte das anders sein?',
    antwort: 'Weil wir mit Ihrer Zahl anfangen, nicht mit einem Versprechen. Im ersten Schritt rechnen wir aus, wie viele Ihrer Beratungen heute zu Behandlungen werden. Wenn diese Zahl gut ist, brauchen Sie uns nicht.',
  },
  {
    frage: 'Wenn meine Mitarbeiter es nicht nutzen, bringt es nichts.',
    antwort: 'Richtig. Deshalb richten wir es mit Ihrem Team ein und nicht für es. Die Rezeption arbeitet weiter im Terminkalender, nur dass Anfragen, Formulare und Nachfassen dort mit ankommen statt daneben.',
  },
  {
    frage: 'Was ist mit Datenschutz und Werberecht?',
    antwort: 'Für die erste Auswertung brauchen wir vier Spalten: Patientenkennung, Termindatum, Leistungsname und Status. Keine Namen, keine Befunde. Alles Weitere läuft DSGVO-konform, und Werbung für ästhetische Behandlungen richtet sich nach dem Heilmittelwerbegesetz.',
  },
];

/* Ruhiges Einblenden beim Scrollen: acht Pixel Weg, eine halbe Sekunde,
   einmalig. Beide Vorbilder bewegen wenig und dafuer genau - eine Seite, die
   staendig zappelt, wirkt billiger, nicht lebendiger. Wer Bewegung reduziert
   haben moechte, bekommt alles sofort und ohne Weg. */
function useEinblenden(ref) {
  useEffect(() => {
    const wurzel = ref.current;
    if (!wurzel) return undefined;
    const ziele = wurzel.querySelectorAll('[data-einblenden]');
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (ruhig || typeof IntersectionObserver === 'undefined') return undefined;
    // Erst jetzt darf ueberhaupt versteckt werden. Ohne diese Klasse bleibt
    // die Seite sichtbar - waere es umgekehrt, machte ein aussetzender
    // Beobachter die gesamte Seite unsichtbar. Ein Animationsfehler darf nie
    // Inhalt kosten.
    wurzel.classList.add('mit-einblenden');
    // Was beim Laden schon im Bild steht, blendet sofort ein. Wartet der Hero
    // erst auf den Beobachter, setzt die Bewegung sichtbar verzoegert ein und
    // wirkt abgehackt.
    requestAnimationFrame(() => {
      wurzel.querySelectorAll('[data-einblenden]').forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.9) el.classList.add('ist-sichtbar');
      });
    });
    const beobachter = new IntersectionObserver((eintraege) => {
      eintraege.forEach((eintrag) => {
        if (!eintrag.isIntersecting) return;
        eintrag.target.classList.add('ist-sichtbar');
        beobachter.unobserve(eintrag.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    ziele.forEach((el) => beobachter.observe(el));
    // Sicherheitsnetz: Feuert der Beobachter nicht - etwa in einem Tab im
    // Hintergrund - steht nach zwei Sekunden trotzdem alles da.
    const notfall = setTimeout(() => ziele.forEach((el) => el.classList.add('ist-sichtbar')), 2000);
    return () => { beobachter.disconnect(); clearTimeout(notfall); };
  }, [ref]);
}

export default function Aerzte() {
  const wurzel = useRef(null);
  useEinblenden(wurzel);

  useEffect(() => {
    document.title = 'Für Ärzte mit ästhetischen Selbstzahlerbehandlungen | Make It Media';
    if (document.getElementById('mim-fonts')) return undefined;
    const link = document.createElement('link');
    link.id = 'mim-fonts';
    link.rel = 'stylesheet';
    link.href = SCHRIFTEN;
    document.head.appendChild(link);
    return undefined;
  }, []);

  const abschnitt = 'px-5 py-16 sm:px-6 md:px-10 md:py-24 lg:px-14';
  const behaelter = 'mx-auto w-full max-w-[1180px]';
  const h2 = 'text-[30px] font-bold leading-[1.04] tracking-[-0.035em] text-[#0D2249] sm:text-[38px] md:text-[48px]';
  const augenbraue = 'text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]';

  return (
    <div
      ref={wurzel}
      className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#F8F9FB] text-[#0D2249] antialiased"
      style={{ fontFamily: SANS }}
    >
      <style>{`
        /* Der Eindruck von Bewegung entsteht nicht durch grosse Effekte,
           sondern dadurch, dass viele kleine Teile kurz nacheinander
           erscheinen. Kurze Wege, kurze Dauer, enger Versatz - lang und weit
           wirkt zaeh, nicht dynamisch. */
        .mit-einblenden [data-einblenden] {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity .42s cubic-bezier(.22,1,.36,1), transform .42s cubic-bezier(.22,1,.36,1);
        }
        .mit-einblenden [data-einblenden].ist-sichtbar { opacity: 1; transform: none; }

        /* Kinder eines eingeblendeten Bereichs folgen versetzt. Die Stufe
           steht am Element, damit die Reihenfolge im Quelltext lesbar bleibt. */
        .mit-einblenden [data-einblenden] [data-stufe] {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity .42s cubic-bezier(.22,1,.36,1), transform .42s cubic-bezier(.22,1,.36,1);
        }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe] { opacity: 1; transform: none; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe="1"] { transition-delay: .04s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe="2"] { transition-delay: .1s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe="3"] { transition-delay: .16s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe="4"] { transition-delay: .22s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe="5"] { transition-delay: .28s; }

        /* Die Systemgrafik kommt von rechts herein und richtet sich auf. */
        .mit-einblenden [data-einblenden] [data-visual] {
          opacity: 0;
          transform: translateX(28px) scale(.97);
          transition: opacity .55s cubic-bezier(.22,1,.36,1) .12s, transform .55s cubic-bezier(.22,1,.36,1) .12s;
        }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-visual] { opacity: 1; transform: none; }

        /* Die Zeilen im Ablauf laufen von oben nach unten durch. */
        .mit-einblenden [data-einblenden] [data-stufe-visual] {
          opacity: 0;
          transform: translateY(10px);
          transition: opacity .34s cubic-bezier(.22,1,.36,1), transform .34s cubic-bezier(.22,1,.36,1);
        }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual] { opacity: 1; transform: none; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual="1"] { transition-delay: .34s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual="2"] { transition-delay: .42s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual="3"] { transition-delay: .5s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual="4"] { transition-delay: .58s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-stufe-visual="5"] { transition-delay: .66s; }

        /* Karten und Kacheln erscheinen der Reihe nach statt alle zugleich.
           Ab dem sechsten Element laeuft der Versatz aus, sonst wartet man am
           Ende einer langen Liste zu lange auf das letzte Stueck.

           Bewusst mit Uebergang und nicht mit Keyframes: In einem Tab im
           Hintergrund pausieren Animationen, und mit fill-mode "both" bliebe
           der Anfangszustand stehen - die Karten waeren dann unsichtbar. Ein
           Uebergang endet auch dann beim Zielwert. */
        .mit-einblenden [data-einblenden] [data-folge] > * {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity .42s cubic-bezier(.22,1,.36,1), transform .42s cubic-bezier(.22,1,.36,1);
        }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > * { opacity: 1; transform: none; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(1) { transition-delay: .03s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(2) { transition-delay: .09s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(3) { transition-delay: .15s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(4) { transition-delay: .21s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(5) { transition-delay: .27s; }
        .mit-einblenden [data-einblenden].ist-sichtbar [data-folge] > *:nth-child(n+6) { transition-delay: .33s; }

        .mim-display, h1, h2, h3 { font-family: ${DISPLAY}; }
        .growth-float { animation: growthFloat 6s ease-in-out infinite; }
        .growth-float-delayed { animation: growthFloat 6s ease-in-out -2.4s infinite; }
        @keyframes growthFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
        @media (prefers-reduced-motion: reduce) {
          .mit-einblenden [data-einblenden],
          .mit-einblenden [data-einblenden] [data-stufe],
          .mit-einblenden [data-einblenden] [data-visual],
          .mit-einblenden [data-einblenden] [data-stufe-visual],
          .mit-einblenden [data-einblenden] [data-folge] > * {
            opacity: 1; transform: none; transition: none; animation: none;
          }
          .growth-float, .growth-float-delayed { animation: none; }
        }
      `}</style>
      <header className="sticky top-0 z-40 w-full max-w-full overflow-hidden border-b border-[#0D2249]/8 bg-[#F8F9FB]/90 backdrop-blur-xl" data-testid="aerzte-header">
        <div className={`${behaelter} flex h-[72px] min-w-0 items-center justify-between gap-2 px-5 sm:px-6`}>
          <span
            className="mim-display shrink-0 text-base font-bold tracking-[-0.035em] sm:text-lg"
            
          >
            Make It Media
          </span>
          <nav className="hidden items-center gap-7 text-sm font-medium text-[#3B4962] md:flex" aria-label="Seitennavigation">
            <a href="#beweis" className="transition hover:text-[#CC3366]">Nachweis</a>
            <a href="#ablauf" className="transition hover:text-[#CC3366]">Ablauf</a>
            <a href="#fragen" className="transition hover:text-[#CC3366]">Fragen</a>
          </nav>
          <a href="#anfrage" className="inline-flex h-10 shrink-0 items-center rounded-full bg-[#0D2249] px-4 text-xs font-semibold text-white transition hover:bg-[#CC3366] sm:px-5 sm:text-sm" data-testid="nav-cta">
            <span className="sm:hidden">Auswertung</span><span className="hidden sm:inline">Kostenlose Auswertung</span>
          </a>
        </div>
      </header>

      {/* Hero: Klarheit, Zielgruppe, eine Handlung - und der Rechner statt
          einer Produktabbildung. */}
      {/* Randlos statt Kasten. Ein gerahmter Hero wirkt wie ein Banner auf einer
          Seite; ein randloser wirkt wie die Seite selbst. Die Vorbilder machen
          es genauso - dort laeuft der Hero ueber die volle Breite und traegt
          eine sehr grosse, sehr eng gesetzte Ueberschrift. */}
      <section id="top" className="px-2 pt-2 sm:px-4 sm:pt-4" data-testid="hero-section">
        <div className="relative overflow-hidden rounded-[24px] bg-[#0D2249] text-white sm:rounded-[36px]">
          {/* Bewegtes Bild als Hintergrund, sehr leise. Es soll Tiefe geben
              und nicht vom Text ablenken, deshalb liegt eine dunkle Schicht
              darueber. Wer Bewegung reduziert haben moechte, bekommt nur das
              Standbild. */}
          <video
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.22] motion-reduce:hidden"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay muted loop playsInline preload="metadata"
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0D2249]/92 via-[#0D2249]/80 to-[#0D2249]/95" aria-hidden="true" />
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ backgroundImage: 'radial-gradient(circle at 12% 0%, rgba(204,51,102,.5), transparent 34%), radial-gradient(circle at 92% 6%, rgba(255,255,255,.14), transparent 30%)' }}
            aria-hidden="true"
          />
        <div className={`relative ${behaelter} px-5 pb-20 pt-14 sm:px-6 md:px-10 md:pb-28 md:pt-20 lg:px-14`}>
          <div className="grid min-w-0 items-center gap-14 lg:grid-cols-12 lg:gap-12">
            <div className="min-w-0 lg:col-span-7" data-einblenden>
              <span className="inline-flex max-w-full items-center gap-2 whitespace-normal rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-[11px] font-medium leading-snug text-white/85 sm:text-xs" data-stufe="1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F3A7C1]" />
                Für Ärzte mit ästhetischen Selbstzahlerbehandlungen
              </span>

              {/* Gross und leicht statt klein und fett: Die Vorbilder setzen ihre
                  Ueberschrift bei 72 Pixel im halbfetten Schnitt mit -4 Prozent
                  Laufweite. Das wirkt souveraen, waehrend eine kleine fette
                  Zeile laut wirkt. */}
              <h1 className="mt-8 max-w-3xl break-words text-[40px] font-semibold leading-[0.96] tracking-[-0.045em] sm:text-[58px] lg:text-[74px]" data-stufe="2">
                Genau die Behandlungen auslasten,{' '}
                <span className="text-[#F3A7C1]">mit denen Ihre Praxis wachsen soll.</span>
              </h1>

              <p className="mt-7 max-w-xl text-[15px] leading-relaxed text-white/75 sm:text-[17px]" data-stufe="3">
                Wir erzeugen gezielt Nachfrage dafür, begleiten jede Anfrage bis zum
                Behandlungstermin und zeigen Ihnen am Ende,{' '}
                <span className="font-semibold text-white">welche Maßnahme welchen Eingriff gebracht hat.</span>
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3" data-stufe="4">
                <a href="#anfrage" className="inline-flex items-center rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#0D2249] shadow-lg transition duration-200 hover:-translate-y-0.5 hover:bg-[#F2F1EF]">
                  Kostenlose Auswertung <ArrowUpRight className="ml-2 h-4 w-4" />
                </a>
                <a href="#rechner" className="inline-flex items-center rounded-full border border-white/20 px-6 py-3.5 text-sm font-medium text-white/80 transition duration-200 hover:border-white/40 hover:text-white">
                  Potenzial überschlagen
                </a>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-white/65 sm:text-sm" data-stufe="5">
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F3A7C1]" />Sechs betreute Praxen</span>
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F3A7C1]" />DSGVO-konform</span>
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F3A7C1]" />Kostenlose Erstauswertung</span>
              </div>
            </div>

            <div className="min-w-0 lg:col-span-5" data-einblenden>
              <div data-visual className="py-14 sm:py-16">
                <GrowthSystemVisual />
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      <ReferenceWall />
      <EvidenceSection />

      <section id="rechner" className={abschnitt} data-testid="calculator-section">
        <div className={behaelter} data-einblenden>
          <div className="grid min-w-0 gap-10 rounded-[26px] bg-[#F2F1EF] p-6 sm:p-9 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:p-12">
            <div className="min-w-0 max-w-md">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#CC3366]">Ihre Zahlen statt unserer Versprechen</p>
              <h2 className="mt-4 text-[30px] font-bold leading-[1.02] tracking-[-0.035em] sm:text-[40px]">
                Was würden 13 zusätzliche Abschlüsse aus 100 Beratungen verändern?
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-[#3B4962]">
                Der Rechner nutzt Ihre Beratungen, Ihre Quote und Ihren durchschnittlichen
                Behandlungswert. Das Ergebnis sehen Sie vor jeder Kontaktabfrage.
              </p>
            </div>
            <ConversionCalculator />
          </div>
        </div>
      </section>
      <ConfessionSection />
      <FounderSection />
      <div id="ablauf"><ProcessSection /></div>
      <ComparisonSection />
      <IncludedSection />

      {/* Für wen - und ausdrücklich für wen nicht. Eine Seite, die niemanden
          ausschließt, spricht niemanden an. */}
      <section id="fuer-wen" className={abschnitt} data-testid="audience-section">
        <div className={behaelter} data-einblenden>
          <p className={augenbraue}>Passt das zu Ihnen?</p>
          <h2 className={`mt-4 ${h2}`}>Wir sind nicht für jede Praxis richtig.</h2>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-[28px] border border-[#CC3366]/25 bg-white p-6 shadow-[0_22px_60px_-46px_rgba(13,34,73,0.45)] sm:p-8">
              <p className="text-lg font-bold tracking-[-0.025em]">Passt, wenn</p>
              <ul className="mt-4 space-y-3">
                {FUER_WEN.map((punkt) => (
                  <li key={punkt} className="flex gap-3 text-[15px] leading-relaxed text-[#3B4962]">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FBE8EF] text-[#CC3366]"><Check className="h-3 w-3" /></span>
                    {punkt}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-[28px] bg-[#0D2249] p-6 text-white shadow-[0_22px_60px_-42px_rgba(13,34,73,0.65)] sm:p-8">
              <p className="text-lg font-bold tracking-[-0.025em]">Passt nicht, wenn</p>
              <ul className="mt-4 space-y-3">
                {NICHT_FUER.map((punkt) => (
                  <li key={punkt} className="flex gap-3 text-[15px] leading-relaxed text-white/65">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-[#F3A7C1]" />
                    {punkt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <RiskReversalSection />

      {/* Die Fragen stammen aus der Zielgruppenanalyse, nicht aus der
          Vorstellung davon, was jemand fragen könnte. */}
      <section id="fragen" className={abschnitt} data-testid="faq-section">
        <div className={behaelter} data-einblenden>
          <p className={augenbraue}>Häufige Fragen</p>
          <h2 className={`mt-4 ${h2}`}>Was vor einer Entscheidung noch wichtig ist.</h2>

          <div className="mt-10 grid gap-3 lg:grid-cols-2 lg:items-start">
            {FRAGEN.map((eintrag) => (
              <details key={eintrag.frage} className="group rounded-[22px] border border-[#0D2249]/10 bg-white p-5 transition open:border-[#CC3366]/35 open:shadow-[0_18px_45px_-36px_rgba(13,34,73,0.55)]">
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-[15px] font-medium sm:text-base">
                  {eintrag.frage}
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FBE8EF] text-[#CC3366] transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-[#3B4962]">{eintrag.antwort}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Abschluss: Kosten des Nichtstuns, dann eine einzige Handlung. */}
      <section id="anfrage" className={abschnitt} data-testid="closing-section">
        <div className={behaelter} data-einblenden>
          <div className="relative overflow-hidden rounded-[30px] bg-[#0D2249] px-6 py-12 text-white sm:px-10 md:rounded-[36px] md:px-14 md:py-16">
            <div className="absolute -right-20 -top-24 h-80 w-80 rounded-full border-[58px] border-[#CC3366]/20" aria-hidden="true" />
            <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#F3A7C1] sm:text-sm">
              Wenn nichts passiert
            </p>
            <h2
              className="mt-4 max-w-3xl text-[32px] font-bold leading-[1.04] tracking-[-0.04em] sm:text-[42px] md:text-[54px]"
              
            >
              Die Beratungen von letzter Woche bleiben unbeantwortet.
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/75 sm:text-base">
              Sie haben beraten, aufgeklärt und Zeit investiert. Ob daraus eine Behandlung wurde,
              steht heute in keiner Auswertung. Das ändern wir zuerst, bevor wir über Werbung reden.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="#top"
                className="inline-flex h-13 items-center justify-center rounded-full bg-[#CC3366] px-7 py-4 text-[15px] font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#B52A5A]"
                data-testid="closing-cta"
              >
                Kostenlose Auswertung anfordern
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </a>
              <span className="text-sm text-white/60">
                Kostenlos · 30 Minuten · Sie bekommen Ihre Auswertung, auch ohne Zusammenarbeit
              </span>
            </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#0D2249]/10 bg-[#F2F1EF] px-5 py-10 sm:px-6 md:px-10 lg:px-14">
        <div className={`${behaelter} flex flex-col gap-4 text-sm text-[#68748A] sm:flex-row sm:items-center sm:justify-between`}>
          <span className="mim-display font-bold tracking-[-0.035em] text-[#0D2249]">
            Make It Media
          </span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <a href="mailto:info@makeit-media.de" className="flex items-center gap-1.5 hover:text-[#0D2249]">
              <Mail className="h-4 w-4" />info@makeit-media.de
            </a>
            <a href="/impressum" className="hover:text-[#0D2249]">Impressum</a>
            <a href="/datenschutz" className="hover:text-[#0D2249]">Datenschutz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
